import { useCallback, useEffect, useState } from 'react'

import { usePSNavigate } from '../../hooks/usePSNavigate'
import { Reader } from '../../models/Reader'
import { RecordFactory } from '../../models/RecordFactory'
import { Source } from '../../models/Source'
import { Taxcode, isProductTaxable, isShippingTaxable } from '../../models/Taxcode'
import { isValid } from '../../utils'
import { currencyFormat, filterNumericInput } from '../../utils/numbers'
import { ComponentData } from '../../utils/typing'
import { PocketComponent, PocketComponentProps, usePSComponent } from '../PocketComponent'
import { Spinner } from '../Spinner'
import Button from '../app/design/Button'
import Label from '../app/design/Label'
import Modal from '../app/design/Modal'
import { SourceModalWorkflow } from '../contacts/SourceModalWorkflow'
import { Select } from '../common/fields/Select'
import { TextField } from '../common/fields/TextField'
import { readerTypeOptions } from '../settings/Options'
import { WorkflowPage } from '../workflows/WorkflowPage'

const viewWillAppear = async (base: PocketComponent) => {
    const reader = new Reader()
    reader.type = 'bbpos_wisee'
    reader.line1 = base.me.line1
    reader.line2 = base.me.line2
    reader.city = base.me.city
    reader.state = base.me.state
    reader.postalCode = base.me.postalCode

    let taxcode = undefined
    const result = await RecordFactory.getInstance().queryTaxcode(reader.location)
    if (result.ok) {
        taxcode = result.value || undefined
        reader.state = result.value?.state || '' // always pull state from taxcode
    }
    const sources = await RecordFactory.getInstance().loadSources()

    return { reader, sources, taxcode }
}

const render = ({ base, data }: PocketComponentProps<ComponentData<typeof viewWillAppear>>) => {
    const READER_COST = 250
    const SHIPPING_COST = 13
    const navigate = usePSNavigate()
    const user = base.me

    const [reader, setReader] = useState(data.reader.clone())
    const [chargeEnabled, setChargeEnabled] = useState(false)
    const [currentSource, setCurrentSource] = useState<Source | null>(null)
    const [quantity, setQuantity] = useState(1)
    const [taxcode, setTaxcode] = useState<Taxcode | undefined>(data.taxcode)
    const [lastSearched, setLastSearched] = useState(reader.location)
    const [showConfirmModal, setShowConfirmModal] = useState(false)
    const [isProcessing, setIsProcessing] = useState(false)

    const postalCodeValidation = reader.postalCode?.match(/\d{5}/) ? undefined : 'error'
    const streetValidation = reader.line1?.replace(/\s/g, '').length ? undefined : 'error'
    const [isPaymentMethodsModalOpen, setIsPaymentMethodsModalOpen] = useState(false)
    const [sources, setSources] = useState(data.sources)

    const searchTaxcode = async (location: string) => {
        const result = await RecordFactory.getInstance().queryTaxcode(location)
        if (result.ok) {
            setTaxcode(result.value || undefined)
            setReader(reader.clone({ state: result.value?.state || '' }))
            return result.value || undefined
        }
        return undefined
    }

    useEffect(() => {
        setChargeEnabled(
            isValid(postalCodeValidation) &&
                isValid(streetValidation) &&
                !!currentSource &&
                currentSource.id !== 'new'
        )
    }, [postalCodeValidation, streetValidation, currentSource])

    const costSummaryString = (quantity: number, taxcode?: Taxcode, state?: string) => {
        const taxRate = taxcode?.rate || 0
        const rate = taxRate / 100
        const lineTotal = READER_COST * quantity
        let tax = (isProductTaxable(state || '') ? lineTotal : 0) * rate
        tax += (isShippingTaxable(state || '') ? SHIPPING_COST : 0) * rate
        const totalStr = currencyFormat(lineTotal + tax + SHIPPING_COST)
        let extraStr = ''
        if (tax > 0) {
            extraStr = `, ${currencyFormat(tax)} tax`
        }
        return `${totalStr} (incl. ${currencyFormat(SHIPPING_COST)} shipping${extraStr})`
    }

    const costSummary = costSummaryString(quantity, taxcode, reader.state)

    const doSearchTaxcode = () => {
        if (lastSearched === reader.location) return
        searchTaxcode(reader.location)
        setLastSearched(reader.location)
    }

    const onPurchase = useCallback(async () => {
        setShowConfirmModal(true)
        setIsProcessing(true)
        // Always do one final taxcode search before purchase to ensure we have the correct charge amount
        const updatedTaxcode = await searchTaxcode(reader.location)
        setTaxcode(updatedTaxcode)
        setIsProcessing(false)
    }, [reader, currentSource, quantity, taxcode])

    const handleConfirmPurchase = async () => {
        if (!currentSource || currentSource.id === 'new') return

        setIsProcessing(true)
        try {
            await RecordFactory.getInstance().purchaseReader(
                reader,
                currentSource,
                quantity,
                taxcode?.id
            )
            navigate(-1)
        }
        catch (error) {
            base.logAndDisplayError(error)
        }
        finally {
            setIsProcessing(false)
            setShowConfirmModal(false)
        }
    }

    const handleDeleteSource = async (source: Source) => {
        if (source.id === undefined) {
            return
        }
        await RecordFactory.getInstance().deleteSource(source.id)
        setSources((prevSources) => prevSources.filter((s) => s.id !== source.id))
    }

    return (
        <WorkflowPage
            title="Order a card reader"
            footerContent={
                <div className="flex w-full items-center justify-end gap-x-4">
                    <Button intent="primary" disabled={!chargeEnabled} onClick={onPurchase}>
                        Purchase
                    </Button>
                </div>
            }
        >
            <div className="mt-12 flex w-full flex-col items-center justify-start gap-y-6">
                <WorkflowPage.Card title="Order details">
                    <WorkflowPage.IconSection title="Choose a card reader" icon="cardReader.large">
                        <Select
                            size="medium"
                            className="border-gray-500"
                            options={readerTypeOptions().map((option) => ({
                                id: option.value,
                                title: option.label,
                            }))}
                            onSelectOption={(selection) =>
                                setReader(reader.clone({ type: selection.id }))
                            }
                            selectedOptionID={reader.type}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Quantity" icon="hash.large">
                        <TextField
                            size="medium"
                            placeholder="Enter quantity"
                            value={quantity.toString()}
                            onChange={(e) => filterNumericInput(e.target.value, setQuantity)}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Cost" disabled={true} icon="dollar.large">
                        <TextField
                            size="medium"
                            placeholder="Enter cost"
                            value={costSummary}
                            disabled
                            className="text-gray-400"
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection
                        title="Payment methods"
                        icon="cards.large"
                        description={
                            !!currentSource?.id
                                ? currentSource.displayName
                                : 'Select payment method'
                        }
                        descriptionClassName="text-secondary-400"
                        hasHoverEffect
                        secondaryView={
                            <Button
                                onClick={() => setIsPaymentMethodsModalOpen(true)}
                                intent="tertiaryLink"
                                className="hover:text-primary-700/60 mr-2 translate-x-3"
                                size="small"
                            >
                                Edit
                            </Button>
                        }
                    />
                </WorkflowPage.Card>

                <WorkflowPage.Card title="Shipping details">
                    <WorkflowPage.IconSection title="Street" icon="globeClock.large">
                        <TextField
                            size="medium"
                            placeholder="Add street"
                            value={reader.line1}
                            onChange={(e) => setReader(reader.clone({ line1: e.target.value }))}
                            onBlur={doSearchTaxcode}
                            hasError={!!streetValidation}
                            onClear={() => setReader(reader.clone({ line1: '' }))}
                        />
                        {!!streetValidation && (
                            <Label size="sm" intent="danger">
                                A street address is required
                            </Label>
                        )}
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Unit" icon="hash.large">
                        <TextField
                            size="medium"
                            placeholder="Add unit number"
                            value={reader.line2}
                            onChange={(e) => setReader(reader.clone({ line2: e.target.value }))}
                            onBlur={doSearchTaxcode}
                            onClear={() => setReader(reader.clone({ line2: '' }))}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="City" icon="markerPin.large">
                        <TextField
                            size="medium"
                            placeholder="Add city"
                            value={reader.city}
                            onChange={(e) => setReader(reader.clone({ city: e.target.value }))}
                            onBlur={doSearchTaxcode}
                            onClear={() => setReader(reader.clone({ city: '' }))}
                        />
                    </WorkflowPage.IconSection>

                    <WorkflowPage.IconSection title="Postal code" icon="globeClock.large">
                        <TextField
                            size="medium"
                            placeholder="Add zipcode"
                            mask="00000"
                            value={reader.postalCode}
                            onChange={(e) =>
                                setReader(reader.clone({ postal_code: e.target.value }))
                            }
                            onBlur={doSearchTaxcode}
                            hasError={!!postalCodeValidation}
                            onClear={() => setReader(reader.clone({ postal_code: '' }))}
                        />
                        {!!postalCodeValidation && (
                            <Label size="sm" intent="danger">
                                That does not appear to be a valid zipcode.
                            </Label>
                        )}
                    </WorkflowPage.IconSection>
                </WorkflowPage.Card>
            </div>

            <SourceModalWorkflow
                isOpen={isPaymentMethodsModalOpen}
                onClose={() => setIsPaymentMethodsModalOpen(false)}
                sources={sources ?? []}
                onSelectSource={(source) => {
                    setCurrentSource(source), setIsPaymentMethodsModalOpen(false)
                }}
                selectedSourceID={currentSource?.id}
                onCreateSource={{
                    action: 'create-source',
                    client: user,
                    callback: (source) => {
                        setSources([...sources, source])
                        setCurrentSource(source)
                    },
                }}
                onDeleteSource={handleDeleteSource}
            />

            <Modal
                isOpen={showConfirmModal}
                title="Order reader"
                onClose={() => setShowConfirmModal(false)}
                footer={
                    <div className="flex w-full items-center justify-end gap-x-4">
                        <Button intent="secondary" onClick={() => setShowConfirmModal(false)}>
                            Cancel
                        </Button>
                        <Button
                            intent="primary"
                            onClick={handleConfirmPurchase}
                            disabled={isProcessing}
                        >
                            Yes, place order
                        </Button>
                    </div>
                }
            >
                {isProcessing ? (
                    <div className="flex w-full items-center justify-center">
                        <Spinner size={48} />
                    </div>
                ) : (
                    <p>
                        You're about to order a card reader for a total amount of{' '}
                        {costSummaryString(quantity, taxcode, reader.state)}. Do you want to confirm
                        this order?
                    </p>
                )}
            </Modal>
        </WorkflowPage>
    )
}

export const OrderReaderPage = usePSComponent(render, viewWillAppear)
